import { useSelector } from 'react-redux';
import {
  selectDocumentationActiveTabElements,
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../../reducers/documentation/documentationReducer';
import { DocumentationTabTypes } from '../../../../types/enums/DocumentationTabTypes';
import {
  filterFields,
  filterItemsByQuerySearch,
  filterRelatedObjects,
  filterRules,
  filterSweepAutomations,
  filterDedupeAndMatching,
  rulePropsToFilter,
} from './filtersOptions';
import { ActiveState } from '../../activity-state/helpers';
import { ConfigurationItem } from '../../dependencies/DependenciesTypes';
import { useMemo, useState, useEffect } from 'react';
import { selectTopBarSearchText } from '../../../../reducers/documentation/documentationTopBarReducer';
import { useDocumentationFiltersContext } from './DocumentationFiltersContext';
import { DocumentationFiltersTypesMap } from './filterTypes';
import { FilterComponentType } from '../../../common/filters/filters-renderer/types';

const typeFilterMock = {
  type: FilterComponentType.MULTI_SELECT,
  label: '',
  selectedValues: [],
  isAllSelected: true,
};

// Expensive tabs that need lazy loading
const EXPENSIVE_TABS = [
  DocumentationTabTypes.RELATED_OBJECTS,
  DocumentationTabTypes.SF_AUTOMATIONS,
  DocumentationTabTypes.VALIDATION_RULES,
];

export const useFilterTabs = () => {
  const elements = useSelector(selectDocumentationActiveTabElements);
  const activeTab = useSelector(selectSingleObjectActiveTab);
  const objectTypeName = useSelector(selectSingleObject);
  const searchTxt = useSelector(selectTopBarSearchText);

  const { filterValues } = useDocumentationFiltersContext();

  // State for lazy loading expensive operations
  const [isFiltering, setIsFiltering] = useState(false);
  const [filteredCache, setFilteredCache] = useState<{ [key: string]: ConfigurationItem[] }>({});

  // Ensure we always have a safe array to work with
  const safeElements = useMemo(() => elements ?? [], [elements]);

  const selectedRecordTypesData = filterValues?.[DocumentationFiltersTypesMap.recordTypes];
  const selectedStepsData = filterValues?.[DocumentationFiltersTypesMap.steps];
  const selectedAutomationTypesData = filterValues?.[DocumentationFiltersTypesMap.automationTypes];
  const selectedApexTypesData = filterValues?.[DocumentationFiltersTypesMap.apexTypes];
  const selectedActiveOnly = filterValues?.[DocumentationFiltersTypesMap.activeState]
    ? filterValues[DocumentationFiltersTypesMap.activeState].selectedValues[0] ===
      ActiveState.activeOnly
    : true;
  const selectedConfigurationTypes = filterValues?.[DocumentationFiltersTypesMap.configurationType];
  const selectedRelatedObjects = filterValues?.[DocumentationFiltersTypesMap.relatedObject];
  const selectedFieldTypes =
    filterValues?.[DocumentationFiltersTypesMap.fieldTypes]?.selectedValues;
  const selectedUtilization =
    filterValues?.[DocumentationFiltersTypesMap.utilization]?.selectedValues;
  const selectedActionTypes = filterValues?.[DocumentationFiltersTypesMap.actionTypes];
  const selectedOperations = filterValues?.[DocumentationFiltersTypesMap.operations];
  const selectedStatus = filterValues?.[DocumentationFiltersTypesMap.status];

  const _searchTxt = searchTxt ?? '';
  const objectType = objectTypeName?.objectType ?? '';

  // Create cache key for memoization - optimized to avoid expensive JSON.stringify
  const cacheKey = useMemo(() => {
    const filterKeys = [
      selectedRecordTypesData?.selectedValues?.join(',') ?? '',
      selectedStepsData?.selectedValues?.join(',') ?? '',
      selectedAutomationTypesData?.selectedValues?.join(',') ?? '',
      selectedApexTypesData?.selectedValues?.join(',') ?? '',
      selectedActiveOnly ? 'active' : 'all',
      selectedConfigurationTypes?.selectedValues?.join(',') ?? '',
      selectedRelatedObjects?.selectedValues?.join(',') ?? '',
      selectedFieldTypes?.join(',') ?? '',
      selectedUtilization?.join(',') ?? '',
    ].join('|');

    return `${activeTab}-${_searchTxt}-${filterKeys}-${objectType}`;
  }, [
    activeTab,
    _searchTxt,
    objectType,
    selectedRecordTypesData?.selectedValues,
    selectedStepsData?.selectedValues,
    selectedAutomationTypesData?.selectedValues,
    selectedApexTypesData?.selectedValues,
    selectedActiveOnly,
    selectedConfigurationTypes?.selectedValues,
    selectedRelatedObjects?.selectedValues,
    selectedFieldTypes,
    selectedUtilization,
  ]);

  const cachedResult = filteredCache[cacheKey];

  const baseFilteredElements = useMemo(() => {
    if (!_searchTxt.trim()) {
      return safeElements;
    }

    return filterItemsByQuerySearch({
      itemProps: rulePropsToFilter,
      searchTxt: _searchTxt,
      items: safeElements,
    }) as ConfigurationItem[];
  }, [_searchTxt, safeElements]);

  // For expensive tabs, use lazy loading
  useEffect(() => {
    if (!activeTab || !EXPENSIVE_TABS.includes(activeTab) || cachedResult) {
      return; // Skip lazy loading for fast tabs or if we have cached results
    }

    setIsFiltering(true);

    // Use setTimeout to ensure this runs after the current execution context
    const timeoutId = setTimeout(() => {
      const safeBaseElements = baseFilteredElements ?? [];
      let result: ConfigurationItem[] = [];

      try {
        switch (activeTab) {
          case DocumentationTabTypes.RELATED_OBJECTS: {
            // This is the most expensive operation
            result = filterRelatedObjects({
              items: safeBaseElements,
              selectedConfigurationTypes,
              selectedActiveState:
                filterValues?.[DocumentationFiltersTypesMap.activeState]?.selectedValues[0],
              selectedRelatedObjects,
              excludedFromReferenceObjectName: objectType,
            });
            break;
          }

          case DocumentationTabTypes.SF_AUTOMATIONS:
          case DocumentationTabTypes.VALIDATION_RULES: {
            result = filterRules({
              rules: safeBaseElements,
              selectedStepValuesData: selectedStepsData,
              selectedTypeValuesData: selectedApexTypesData ?? selectedAutomationTypesData,
              selectedRecordTypeValuesData: selectedRecordTypesData ?? typeFilterMock,
            });
            break;
          }

          default:
            result = safeBaseElements;
        }

        // Cache the result and update state
        setFilteredCache((prev) => ({
          ...prev,
          [cacheKey]: result,
        }));
      } catch (error) {
        console.error('Error filtering elements:', error);
        setFilteredCache((prev) => ({
          ...prev,
          [cacheKey]: safeBaseElements,
        }));
      } finally {
        setIsFiltering(false);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [
    activeTab,
    cacheKey,
    cachedResult,
    baseFilteredElements,
    selectedConfigurationTypes,
    filterValues,
    selectedRelatedObjects,
    objectType,
    selectedStepsData,
    selectedApexTypesData,
    selectedAutomationTypesData,
    selectedRecordTypesData,
  ]);

  const filteredResults = useMemo(() => {
    if (cachedResult) {
      return cachedResult;
    }

    const safeBaseElements = baseFilteredElements ?? [];

    switch (activeTab) {
      case DocumentationTabTypes.FIELDS: {
        return filterFields({
          fields: safeBaseElements,
          selectedUsageValues: selectedUtilization,
          selectedTypeValues: selectedFieldTypes,
        });
      }

      case DocumentationTabTypes.RECORD_TYPES: {
        return safeBaseElements.filter(
          (rt) =>
            (selectedActiveOnly ? !!rt.isActive : true) &&
            (rt.objectApiNames?.includes(objectType) || rt.objectName === objectType),
        );
      }

      case DocumentationTabTypes.APEX: {
        return filterRules({
          rules: safeBaseElements,
          selectedStepValuesData: selectedStepsData,
          selectedTypeValuesData: selectedApexTypesData ?? selectedAutomationTypesData,
          selectedRecordTypeValuesData: selectedRecordTypesData ?? typeFilterMock,
        });
      }

      case DocumentationTabTypes.RELATED_OBJECTS:
      case DocumentationTabTypes.SF_AUTOMATIONS:
      case DocumentationTabTypes.VALIDATION_RULES: {
        // For expensive tabs without cached results, show base elements while loading
        // This provides better UX than an empty table
        return isFiltering ? safeBaseElements : safeBaseElements;
      }

      case DocumentationTabTypes.SWEEP_AUTOMATIONS:
      case DocumentationTabTypes.PLAYBOOK_ALERTS:
      case DocumentationTabTypes.ASSIGNMENTS:
      case DocumentationTabTypes.SCHEDULED_ASSIGNMENTS: {
        const result = filterSweepAutomations({
          automations: safeBaseElements,
          selectedActionTypesData: selectedActionTypes,
          selectedRecordTypesData: selectedRecordTypesData,
          selectedStepsData: selectedStepsData,
        });
        return result;
      }

      case DocumentationTabTypes.MATCHING_DEDUPE: {
        const isActive = selectedStatus?.selectedValues?.[0] === 'true';
        const allSelectedStatus = !selectedStatus || selectedStatus?.selectedValues?.length === 0;
        const byActiveStatus = allSelectedStatus
          ? safeBaseElements
          : safeBaseElements.filter((automation) =>
              isActive ? automation.isActive : !automation.isActive,
            );

        return filterDedupeAndMatching({
          automations: byActiveStatus,
          selectedActionTypesData: selectedOperations,
          selectedRecordTypesData: selectedRecordTypesData,
          selectedStepsData: selectedStepsData,
        });
      }

      default: {
        return safeBaseElements;
      }
    }
  }, [
    cachedResult,
    activeTab,
    baseFilteredElements,
    selectedUtilization,
    selectedFieldTypes,
    selectedActiveOnly,
    objectType,
    selectedStepsData,
    selectedApexTypesData,
    selectedAutomationTypesData,
    selectedRecordTypesData,
    selectedOperations,
    selectedActionTypes,
    selectedStatus,

    isFiltering,
  ]);

  return {
    results: filteredResults ?? [],
    isLoading: !!activeTab && EXPENSIVE_TABS.includes(activeTab) && isFiltering,
  };
};
